'use server';

import { prisma } from '@/lib/db';
import { sendEmail } from '@/lib/email';
import { type ActionResult, failure, ok } from '@/lib/types/action-result';

import {
    createEmailVerificationToken,
    emailVerificationLimiter,
    getCurrentUserId,
    getMissingSmtpConfig,
    getRequestIp,
    isSmtpConfigured,
    safeLogSystemError,
    verifyEmailTokenIpLimiter,
    verifyEmailTokenLimiter,
} from '../users-shared';

export async function verifyEmailToken(token: string): Promise<ActionResult> {
    try {
        if (!token) return failure('Token fehlt');

        // Code-review S-C (2026-05-13): better-auth's `/verify-email`
        // rate-limit doesn't cover this custom action. Dual-key limiter
        // (token-prefix + IP) bounds both replay of a stolen token and
        // distinct-token brute-force from one IP.
        const ip = await getRequestIp();
        const [{ success: tokenOk }, { success: ipOk }] = await Promise.all([
            verifyEmailTokenLimiter.limit(`verify-email-token:${token.slice(0, 16)}`),
            verifyEmailTokenIpLimiter.limit(`verify-email-ip:${ip}`),
        ]);
        if (!tokenOk || !ipOk) {
            return failure('Zu viele Versuche. Bitte versuchen Sie es später erneut.');
        }

        const verificationToken = await prisma.emailVerificationToken.findUnique({
            where: { token },
        });

        if (!verificationToken) return failure('Token ist ungültig oder abgelaufen');
        if (verificationToken.expiresAt < new Date()) {
            await prisma.emailVerificationToken.delete({ where: { token } });
            return failure('Token ist abgelaufen');
        }

        const user = await prisma.user.findUnique({
            where: { id: verificationToken.userId },
            select: { id: true, pendingEmail: true },
        });

        if (!user) return failure('User nicht gefunden');

        if (user.pendingEmail) {
            const normalizedPending = user.pendingEmail.trim().toLowerCase();
            const existing = await prisma.user.findFirst({
                where: {
                    OR: [
                        { email: normalizedPending },
                        { pendingEmail: normalizedPending },
                    ],
                    NOT: { id: user.id },
                },
            });
            if (existing) return failure('Email wird bereits verwendet');

            await prisma.user.update({
                where: { id: user.id },
                data: { email: normalizedPending, pendingEmail: null, emailVerified: new Date() },
            });
        } else {
            await prisma.user.update({
                where: { id: user.id },
                data: { emailVerified: new Date() },
            });
        }

        await prisma.emailVerificationToken.deleteMany({ where: { userId: verificationToken.userId } });

        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('Email verification failed', e);
        return failure('Fehler bei der Bestätigung');
    }
}

export async function sendVerificationEmail(): Promise<ActionResult> {
    try {
        const userId = await getCurrentUserId();
        if (!userId) return failure('Nicht eingeloggt');

        const user = await prisma.user.findUnique({ where: { id: userId } });
        if (!user) return failure('User nicht gefunden');
        const targetEmail = user.pendingEmail || user.email;
        if (!targetEmail) return failure('Keine E-Mail Adresse hinterlegt');

        const token = await createEmailVerificationToken(user.id);
        const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
        const verificationUrl = `${baseUrl.replace(/\/$/, '')}/verify-email?token=${token}`;

        const emailResult = await sendEmail({
            to: targetEmail,
            subject: 'Bitte E-Mail-Adresse bestaetigen',
            text: `Bitte bestaetigen Sie Ihre E-Mail-Adresse: ${verificationUrl}`,
            html: `<p>Bitte bestaetigen Sie Ihre E-Mail-Adresse:</p><p><a href=\"${verificationUrl}\">${verificationUrl}</a></p>`,
        });
        if (emailResult.error) {
            return failure(emailResult.error);
        }

        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('Verification email failed', e);
        return failure('Fehler beim Senden der Bestaetigungs-E-Mail');
    }
}

export async function requestEmailVerificationByEmail(email: string): Promise<ActionResult> {
    try {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail) return failure('E-Mail fehlt');

        const { success: rateLimitOk } = await emailVerificationLimiter.limit(`email-verify:${normalizedEmail}`);
        if (!rateLimitOk) {
            return failure('Zu viele Anfragen. Bitte versuchen Sie es später erneut.');
        }

        if (!isSmtpConfigured()) {
            return failure(`SMTP nicht konfiguriert (${getMissingSmtpConfig().join(', ')})`);
        }

        const user = await prisma.user.findFirst({
            where: {
                OR: [
                    { email: normalizedEmail },
                    { pendingEmail: normalizedEmail },
                ],
            },
        });
        // Return generic success to prevent email enumeration
        if (!user) return ok();
        const pendingMatch = user.pendingEmail?.trim().toLowerCase() === normalizedEmail;
        if (user.emailVerified && !pendingMatch) return ok();

        const token = await createEmailVerificationToken(user.id);
        const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
        const verificationUrl = `${baseUrl.replace(/\/$/, '')}/verify-email?token=${token}`;

        const emailResult = await sendEmail({
            to: pendingMatch ? user.pendingEmail || email : user.email || email,
            subject: 'Bitte E-Mail-Adresse bestaetigen',
            text: `Bitte bestaetigen Sie Ihre E-Mail-Adresse: ${verificationUrl}`,
            html: `<p>Bitte bestaetigen Sie Ihre E-Mail-Adresse:</p><p><a href=\"${verificationUrl}\">${verificationUrl}</a></p>`,
        });
        if (emailResult.error) {
            return failure(emailResult.error);
        }

        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('Verification email request failed', e, { email });
        return failure('Fehler beim Senden der Bestaetigungs-E-Mail');
    }
}
